/** Parse comma-separated or array keywords into trimmed non-empty strings. */
export function parseKeywordList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((k) => String(k).trim()).filter(Boolean);
  }
  return String(raw)
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);
}

function normalizeKeyword(keyword) {
  return String(keyword || '').trim().toLowerCase();
}

/** Higher score = more accurate keyword match in the user message. */
export function scoreKeywordMatch(query, keyword) {
  const q = String(query || '').toLowerCase().trim();
  const kw = String(keyword || '').trim().toLowerCase();
  if (!kw || !q.includes(kw)) return 0;

  if (q === kw) return 1000 + kw.length;

  const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const wordRe = new RegExp(`(?<!\\w)${escaped}(?!\\w)`, 'i');
  if (wordRe.test(q)) return 500 + kw.length;

  // Weak substring only (e.g. "demo" inside "demonstration")
  return 100 + kw.length * 0.1;
}

export function bestKeywordMatchScore(query, keywords) {
  const list = parseKeywordList(keywords);
  let best = 0;
  for (const kw of list) {
    best = Math.max(best, scoreKeywordMatch(query, kw));
  }
  return best;
}

/** Whole-word or exact match — avoids firing on partial overlaps like "demo" in "demos". */
export function messageMatchesKeywords(query, keywords) {
  return bestKeywordMatchScore(query, keywords) >= 500;
}

export function keywordActionWins(query, primaryKeywords, competingKeywords) {
  const primary = bestKeywordMatchScore(query, primaryKeywords);
  const competing = bestKeywordMatchScore(query, competingKeywords);
  return primary > 0 && primary >= competing;
}

/** Return keywords that appear in both lists (case-insensitive). */
export function findKeywordOverlaps(listA, listB) {
  const setA = new Set(parseKeywordList(listA).map(normalizeKeyword));
  const overlaps = [];
  const seen = new Set();
  for (const kw of parseKeywordList(listB)) {
    const norm = normalizeKeyword(kw);
    if (norm && setA.has(norm) && !seen.has(norm)) {
      seen.add(norm);
      overlaps.push(kw);
    }
  }
  return overlaps;
}

/** Collect all redirect-question keywords for a bot. */
export function collectRedirectKeywords(redirectQuestions, excludeQuestionId = null) {
  if (!Array.isArray(redirectQuestions)) return [];
  return redirectQuestions
    .filter((q) => !excludeQuestionId || q.id !== excludeQuestionId)
    .flatMap((q) => q.keywords || []);
}

export function formatKeywordConflictMessage(overlaps, context = 'Redirect Questions') {
  const list = overlaps.map((k) => `"${k}"`).join(', ');
  return `Keyword conflict: ${list} already used in ${context}. Use unique keywords for popup and redirect triggers.`;
}
